// The rules behind the appointment status picker (TGCLOUD-1751), as pure
// functions so they can be tested at more than one UTC offset without a form.
//
// Two forms write an appointment's status: the pedigree sidebar's Appointment
// form (also the Bulk Edit panel) and the AppBar notification modal. Both render
// `AppointmentStatusField`, which renders these.
//
// THE TIME RULE. A value whose MEANING carries `availableFrom:
// 'appointmentStart'` (Attended and Did Not Attend as shipped, and any site
// value given one of those meanings) is not offered until the appointment has
// started, because nobody can have attended, or failed to attend, an
// appointment that has not happened yet. Cancelled and Moved are always
// offered: cancelling and moving are things done beforehand, and a historic
// appointment can still be recorded as cancelled.
//
// It is a UI rule and nothing more, on purpose. An import, a Desktop migration
// or a correction to a record entered on the wrong day must still be able to
// write any value, so the server does not enforce it. And it NEVER hides or
// blocks the value a record already holds: that value is always selectable, so
// the record re-saves unchanged.

import dayjs from 'dayjs';
import { toLocalDateFromInstant } from 'src/util/partialDate';
import {
  meaningOf,
  meaningRule,
  offeredStatusLabels
} from 'src/util/statusSemantics';

export const STATUS_LIST = 'appointmentStatuses';
export const REASON_LIST = 'appointmentStatusReasons';

const HH_MM = /^\s*(\d{1,2}):(\d{2})/;

/**
 * The hour and minute an appointment's stored time names, or null.
 *
 * `appointmentTime` is stored in three shapes, all of them live: the forms
 * serialise the TimePicker's dayjs value (an ISO instant), Calendly writes the
 * event's `end_time` (an ISO instant), and the demo seed and some imports write
 * a bare "10:30". In a form it may also still be the dayjs object itself. An
 * instant is read by its LOCAL clock, which is the time the clinician picked.
 */
export const timeOfDay = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (dayjs.isDayjs(value)) {
    return value.isValid() ? { h: value.hour(), m: value.minute() } : null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : { h: value.getHours(), m: value.getMinutes() };
  }
  const text = String(value);
  const hhmm = HH_MM.exec(text);
  if (hhmm && !/^\d{4}-/.test(text)) {
    const h = Number(hhmm[1]);
    const m = Number(hhmm[2]);
    return h < 24 && m < 60 ? { h, m } : null;
  }
  const instant = new Date(text);
  return Number.isNaN(instant.getTime())
    ? null
    : { h: instant.getHours(), m: instant.getMinutes() };
};

/**
 * When the appointment starts, as a local Date, or null when it has no date.
 *
 * WHICH PARSER, AND WHY. `appointmentDate` is a `type: Date` column, and the
 * writer that matters here is the form itself: the DateInput hands back a
 * LOCAL-midnight `Date`, and the appointment PUT stores it untouched. That is
 * a local-anchored instant, so CLAUDE.md's rule gives `toLocalDateFromInstant`
 * (local components). `toLocalDate` would read it by its UTC components and
 * put every appointment a day early at any positive offset (Sydney). Calendly
 * and the pathway writer store real instants too, which read the same way.
 *
 * The importer is the exception: a spreadsheet's "2026-03-12" is parsed to UTC
 * midnight. Read locally, that is the previous evening west of Greenwich, so
 * the rule opens a day EARLY for those records. That is the permissive
 * direction: an option becomes available sooner, never later, and the record
 * is never blocked.
 *
 * When the time cannot be read, the start is the beginning of that day.
 */
export const appointmentStart = (appointmentDate, appointmentTime) => {
  const raw =
    dayjs.isDayjs(appointmentDate) && appointmentDate.isValid()
      ? appointmentDate.toDate()
      : appointmentDate;
  const day = toLocalDateFromInstant(raw);
  if (!day) return null;
  const start = new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    0,
    0,
    0,
    0
  );
  const hm = timeOfDay(appointmentTime);
  if (hm) start.setHours(hm.h, hm.m, 0, 0);
  return start;
};

/** Has the appointment started? An appointment with no date cannot say no. */
export const hasStarted = (start, now = new Date()) =>
  !start || now.getTime() >= start.getTime();

/**
 * The options the status picker shows, in order, each marked whether it may be
 * chosen now.
 *
 * @param {object} args
 * @param {Array<{label: string, meaning?: string|null}>} args.rows the enabled
 *   rows, in order (possibly empty before they load)
 * @param {string} args.current what the record holds
 * @param {Date|null} args.start from `appointmentStart`
 * @param {Date} [args.now]
 * @returns {Array<{label: string, meaning: string|null, disabled: boolean,
 *   waitsForStart: boolean, current: boolean}>}
 */
export const statusOptions = ({ rows, current, start, now = new Date() }) => {
  const byLabel = new Map(
    (rows || [])
      .filter((r) => r && r.label)
      .map((r) => [r.label.trim().toLowerCase(), r.meaning || null])
  );
  const started = hasStarted(start, now);
  const currentKey = String(current ?? '')
    .trim()
    .toLowerCase();

  return offeredStatusLabels(rows, current, STATUS_LIST).map((label) => {
    const key = label.trim().toLowerCase();
    const meaning = byLabel.get(key) ?? meaningOf(STATUS_LIST, label);
    const waitsForStart =
      meaningRule(STATUS_LIST, meaning).availableFrom === 'appointmentStart';
    const isCurrent = Boolean(currentKey) && key === currentKey;
    return {
      label,
      meaning,
      waitsForStart,
      current: isCurrent,
      // The record's own value is never blocked, whatever it means.
      disabled: waitsForStart && !started && !isCurrent
    };
  });
};

/** Does this status ask why (cancellation / move reasons)? */
export const statusTakesReason = (status, rows) => {
  const key = String(status ?? '')
    .trim()
    .toLowerCase();
  if (!key) return false;
  const row = (rows || []).find(
    (r) =>
      r &&
      r.label &&
      r.label.trim().toLowerCase() === key &&
      Object.prototype.hasOwnProperty.call(r, 'meaning')
  );
  const meaning = row ? row.meaning : meaningOf(STATUS_LIST, status);
  return Boolean(meaningRule(STATUS_LIST, meaning).takesReason);
};

/**
 * The fields to write when the status changes: the new status, and the reason
 * CLEARED when the new status takes none. A reason left behind on an Attended
 * appointment would say it was cancelled "because the patient was unwell".
 */
export const statusChange = (nextStatus, currentReason, rows) =>
  statusTakesReason(nextStatus, rows)
    ? { appointmentStatus: nextStatus }
    : currentReason
    ? { appointmentStatus: nextStatus, statusReason: '' }
    : { appointmentStatus: nextStatus };

/** Is this a cancellation being recorded after the appointment's time? */
export const cancelledAfterStart = ({
  status,
  rows,
  start,
  now = new Date()
}) => {
  if (!start || !hasStarted(start, now)) return false;
  const key = String(status ?? '')
    .trim()
    .toLowerCase();
  const row = (rows || []).find(
    (r) => r && r.label && r.label.trim().toLowerCase() === key
  );
  const meaning = row?.meaning ?? meaningOf(STATUS_LIST, status);
  return meaning === 'cancelled';
};
